import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import {
   Subject,
   takeUntil,
   switchMap,
   distinctUntilChanged,
   EMPTY,
   map
} from 'rxjs';

import { AiSummaryStoreService } from '../../../services/stores/ai-summary-store.service';
import { PropertyStoreService } from '../../../services/stores/property-store.service';
import { DatePropertyTimezonePipe } from '../../../pipes/date-property-timezone.pipe';
import { TaskUtils } from '../../../util/task-utils';

import { Property } from '../../../domain/property';
import { Task } from '../../../domain/task';
import { WorkflowType } from '../../../domain/enums/workflow-type';
import { ModelSummary } from '../../../domain/Ai/model-summary';
import { AiDocumentType } from '../../../domain/enums/ai-document-type';
import { documentTypeToLabel } from '../../../util/ai-document-type-utils';

@Component({
   selector: 'app-ai-tasks',
   standalone: true,
   imports: [CommonModule, CardModule, ButtonModule, TableModule],
   templateUrl: './ai-tasks.component.html',
   styleUrls: ['./ai-tasks.component.scss'],
   providers: [DatePropertyTimezonePipe]
})
export class AiTasksComponent implements OnDestroy {
   modelSummaries: ModelSummary[] = [];
   loading = true;
   currentProperty: Property | null = null;
   hasNoSummaryData = false;
   errorStatus: number | null = null;

   private destroy$ = new Subject<void>();

   constructor(
      private aiSummaryStore: AiSummaryStoreService,
      private propertyStore: PropertyStoreService,
      private router: Router,
      private datePipe: DatePropertyTimezonePipe
   ) {
      this.propertyStore.currentProperty
         .pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged((a, b) => a?.id === b?.id),
            switchMap((property) => {
               this.currentProperty = property;

               if (!property?.id) {
                  this.resetState();
                  return EMPTY;
               }
               const timezone = this.datePipe.currentPropertyTimezone;
               const yesterday = DateTime.now()
                  .setZone(timezone)
                  .minus({ days: 1 })
                  .toISODate();

               this.loading = true;

               // single subscription chain: property -> summary$
               return this.aiSummaryStore.summary$.pipe(
                  map((aiSummary) => ({ aiSummary, yesterday }))
               );
            })
         )
         .subscribe(({ aiSummary, yesterday }) => {
            if (!aiSummary) {
               this.resetState();
               return;
            }

            const lastSummaryDay =
               aiSummary.summaryResponses?.find((s) => s.date === yesterday) ?? null;

            if (lastSummaryDay?.modelSummaries?.length) {
               this.modelSummaries = lastSummaryDay.modelSummaries
                  .filter((s) => !s.hasError)
                  .map((summary) => ({
                     ...summary,
                     taskName: documentTypeToLabel(summary.documentType as number),
                     atRisk: summary.isFinancial ? summary.amount : 0,
                     statusLabel: summary.isAutoCompleted
                        ? 'Auto Completed'
                        : summary.isValid === false
                          ? 'Alert'
                          : 'At Risk'
                  }));

               this.hasNoSummaryData = false;
               this.errorStatus = null;
            } else {
               this.modelSummaries = [];
               this.hasNoSummaryData = true;
               this.errorStatus = this.aiSummaryStore.getErrorStatus();
            }

            this.loading = false;
         });
   }

   private resetState(): void {
      this.modelSummaries = [];
      this.hasNoSummaryData = true;
      this.errorStatus = null;
      this.loading = false;
   }

   goToTask(summary: ModelSummary): void {
      if (!this.currentProperty) return;

      const task = new Task();
      task.processId = summary.taskInstanceId;
      task.name = documentTypeToLabel(summary.documentType ?? AiDocumentType.Other);
      task.type = WorkflowType.Form;

      TaskUtils.goToTask(task, this.router, this.currentProperty);
   }

   goToTasksManagement(): void {
      this.router.navigate(['/workflow/tasks']);
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   protected readonly documentTypeToLabel = documentTypeToLabel;
}
